"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColorPalette = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const index_js_1 = require("../utils/index.js");
const ColorPicker_js_1 = require("./ColorPicker.js");
const ColorSwatch_js_1 = require("./ColorSwatch.js");
const ColorPickerContext_js_1 = require("./ColorPickerContext.js");
require("@itwin/itwinui-css/css/color-picker.css");
/**
 * `ColorPalette` is used to show a group of `ColorSwatch` components.
 * @example
 * <ColorPalette colors={['#ffffff', '#000000']} />
 * @example
 * <ColorPalette>
 *   <ColorSwatch color='#ffffff' />
 *   <ColorSwatch color='#000000' />
 *   // ...
 * </ColorPalette>
 */
exports.ColorPalette = React.forwardRef((props, ref) => {
    const { colors, label, className, children, ...rest } = props;
    (0, index_js_1.useTheme)();
    const { activeColor, setActiveColor, onChangeComplete } = (0, ColorPickerContext_js_1.useColorPickerContext)();
    const [focusedIndex, setFocusedIndex] = React.useState();
    // callback ref to set tabindex=0 on first child if none of the swatches are tabbable
    const setDefaultTabIndex = (el) => {
        var _a;
        if (el && !el.querySelector('[tabindex="0"]')) {
            (_a = el.firstElementChild) === null || _a === void 0 ? void 0 : _a.setAttribute('tabindex', '0');
        }
    };
    const paletteRef = React.useRef(null);
    const paletteRefs = (0, index_js_1.useMergedRefs)(paletteRef, setDefaultTabIndex);
    // Color palette arrow key navigation
    const handleKeyDown = (event) => {
        if (event.altKey) {
            return;
        }
        const swatches = (0, index_js_1.getFocusableElements)(paletteRef.current);
        if (!swatches.length) {
            return;
        }
        const currentIndex = swatches.findIndex((swatch) => { var _a; return swatch === ((_a = paletteRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument.activeElement); });
        if (currentIndex < 0) {
            return;
        }
        let newIndex = -1;
        switch (event.key) {
            case 'ArrowDown': {
                // Look for next ColorSwatch with same offsetLeft value
                newIndex = swatches.findIndex((swatch, index) => index > currentIndex &&
                    swatch.offsetLeft === swatches[currentIndex].offsetLeft);
                break;
            }
            case 'ArrowUp': {
                // Look backwards for next ColorSwatch with same offsetLeft value
                for (let i = currentIndex - 1; i >= 0; i--) {
                    if (swatches[i].offsetLeft === swatches[currentIndex].offsetLeft) {
                        newIndex = i;
                        break;
                    }
                }
                break;
            }
            case 'ArrowLeft':
                newIndex = Math.max(currentIndex - 1, 0);
                break;
            case 'ArrowRight':
                newIndex = Math.min(currentIndex + 1, swatches.length - 1);
                break;
            case 'Enter':
            case ' ':
            case 'Spacebar':
                swatches[currentIndex].click();
                event.preventDefault();
                return;
        }
        if (newIndex >= 0 && newIndex < swatches.length) {
            setFocusedIndex(newIndex);
            event.preventDefault();
        }
    };
    // call focus() when focusedIndex changes
    React.useEffect(() => {
        var _a;
        if (focusedIndex != null) {
            const swatches = (0, index_js_1.getFocusableElements)(paletteRef.current);
            (_a = swatches[focusedIndex]) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [focusedIndex]);
    return (React.createElement("div", { className: (0, classnames_1.default)('iui-color-palette-wrapper', className), ref: ref, ...rest },
        label && React.createElement("div", { className: 'iui-color-picker-section-label' }, label),
        React.createElement("div", { className: 'iui-color-palette', onKeyDown: handleKeyDown, ref: paletteRefs },
            children,
            colors &&
                colors.map((_color, index) => {
                    const color = (0, ColorPicker_js_1.getColorValue)(_color);
                    return (React.createElement(ColorSwatch_js_1.ColorSwatch, { key: index, color: color, onClick: (event) => {
                            event.preventDefault();
                            onChangeComplete === null || onChangeComplete === void 0 ? void 0 : onChangeComplete(color);
                            setActiveColor(color);
                        }, isActive: color.equals(activeColor) }));
                }))));
});
exports.default = exports.ColorPalette;
